import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";
import { Phone, ArrowRight, ShieldCheck, Star, Check, MapPin, Clock, ThumbsUp, MessageSquare, Hammer, Sparkles } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import ServiceCard from "@/components/ServiceCard";
import TestimonialSlider from "@/components/TestimonialSlider";
import TrustBadges from "@/components/TrustBadges";
import CTAPanel from "@/components/CTAPanel";
import { SERVICES } from "@/data/services";
import { SITE } from "@/data/site";

const HERO_SLIDES = [
  { src: "/IMG-20260606-WA0018.jpg", alt: "Ardeca home remodeling project" },
  { src: "/IMG-20260606-WA0036.jpg", alt: "Ardeca home remodeling project" },
  { src: "/IMG-20260606-WA0054.jpg", alt: "Ardeca home remodeling project" },
  { src: "/IMG-20260606-WA0059.jpg", alt: "Ardeca home remodeling project" },
  { src: "/IMG-20260606-WA0107.jpg", alt: "Ardeca home remodeling project" },
  { src: "/IMG-20260606-WA0120.jpg", alt: "Ardeca home remodeling project" },
  { src: "/IMG-20260606-WA0136.jpg", alt: "Ardeca home remodeling project" },
  { src: "/IMG-20260606-WA0207.jpg", alt: "Ardeca home remodeling project" },
  { src: "/IMG-20260606-WA0223.jpg", alt: "Ardeca home remodeling project" },
];
const ABOUT_IMG = "/house renovation new jersey.webp";
const SHARE_IMG = "/ardeca-kitchen-remodeling-share.jpg";

const STATS = [
  { value: "26+", label: "Years of Experience" },
  { value: "1,200+", label: "NJ Projects Completed" },
  { value: "5.0", label: "Average Client Rating" },
  { value: "100%", label: "Licensed & Insured" },
];

const VALUE_POINTS = [
  "Stress-free remodeling with one accountable point of contact",
  "Premium materials and craftsmanship that stand the test of time",
  "Transparent, itemized pricing — no surprises, no hidden fees",
  "Respectful crews who protect your home and clean up daily",
];

const STEPS = [
  { icon: MessageSquare, title: "1. Tell Us Your Vision", desc: "Share your project and frustrations. We listen first — then offer an honest, no-pressure free quote." },
  { icon: Hammer, title: "2. We Build It Right", desc: "Our skilled family crew handles everything with premium materials, clean job sites, and clear timelines." },
  { icon: ThumbsUp, title: "3. You Love Your Home", desc: "Walk through the finished space, backed by our workmanship and warranties. Stress fixed." },
];

export default function Home() {
  const [slideIndex, setSlideIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setSlideIndex((current) => (current + 1) % HERO_SLIDES.length);
    }, 3000);

    return () => clearInterval(timer);
  }, []);

  return (
    <>
      <Seo
        title="Ardeca Construction | Freehold NJ Remodeling Contractor"
        description="Family-owned Ardeca Construction delivers licensed, insured remodels across Freehold and all of New Jersey. Get a free quote for kitchens, bathrooms, decks, basements, painting and more."
        url="/"
        image={SHARE_IMG}
      />
      {/* ===== HERO ===== */}
      <section data-testid="hero-section" className="relative overflow-hidden bg-brand-creamSoft">
        <div className="pointer-events-none absolute top-0 right-0 h-[480px] w-[480px] rounded-full bg-brand-orange/5 blur-3xl" />
        <div className="container-x relative grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-16 md:py-24">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="inline-flex items-center gap-2 rounded-full bg-white border border-brand-cream px-4 py-2 shadow-sm"
            >
              <ShieldCheck size={15} className="text-brand-orange" />
              <span className="text-xs font-semibold text-brand-dark">Family-Owned · Licensed & Insured · {SITE.yearsExperience} Years</span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
              className="h1 mt-6"
            >
              Your Home,<br />Your Stress — <span className="text-brand-orange">Fixed.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.16, ease: [0.22, 1, 0.36, 1] }}
              className="lead mt-6 max-w-xl"
            >
              From dated kitchens to damp basements, we turn the parts of your home that frustrate you into spaces you'll love.
              <span className="font-semibold text-brand-dark"> {SITE.yearsExperience} years of trusted craftsmanship in Freehold & all of New Jersey.</span>
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
              className="mt-9 flex flex-col sm:flex-row gap-4"
            >
              <Link to="/contact" data-testid="hero-quote-btn" className="btn-cta !py-4 text-base">
                Get Your Free Quote <ArrowRight size={18} />
              </Link>
              <a href={SITE.phoneHref} data-testid="hero-call-btn" className="btn-ghost-dark !py-4 text-base">
                <Phone size={17} /> {SITE.phoneDisplay}
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.36 }}
              className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-brand-muted"
            >
              <span className="flex items-center gap-2"><MapPin size={15} className="text-brand-orange" /> Based in {SITE.baseCity}</span>
              <span className="flex items-center gap-2"><Clock size={15} className="text-brand-orange" /> {SITE.hoursShort}</span>
              <span className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} className="fill-brand-orange text-brand-orange" />)}
                <span className="ml-1 font-medium text-brand-dark">5.0 Rated</span>
              </span>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="relative overflow-hidden rounded-[28px] shadow-2xl aspect-[4/5] sm:aspect-[5/5]">
              <AnimatePresence exitBeforeEnter>
                <motion.img
                  key={slideIndex}
                  src={HERO_SLIDES[slideIndex].src}
                  alt={HERO_SLIDES[slideIndex].alt}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/30 to-transparent" />
            </div>
          </motion.div>
        </div>

        {/* Stats strip */}
        <div className="border-t border-brand-cream bg-white">
          <div className="container-x grid grid-cols-2 md:grid-cols-4 divide-x divide-brand-cream">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08} className="py-7 text-center">
                <p className="text-3xl md:text-4xl font-extrabold text-brand-orange">{s.value}</p>
                <p className="mt-1 text-xs md:text-sm text-brand-muted">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== VALUE PROP ===== */}
      <section data-testid="value-prop" className="container-x py-20 md:py-28">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal className="relative order-2 lg:order-1">
            <div className="overflow-hidden rounded-[28px] shadow-xl aspect-[4/3]">
              <img src={ABOUT_IMG} alt="Skilled Ardeca craftsman at work in New Jersey" className="h-full w-full object-cover" />
            </div>
            <div className="absolute -bottom-6 right-4 sm:-right-6 rounded-2xl bg-brand-dark text-white p-6 max-w-[220px] shadow-xl">
              <p className="text-sm leading-relaxed text-brand-cream">"Our name is on every project. That's why we treat your home like our own."</p>
              <p className="mt-3 text-xs font-semibold text-white/60">— The Ardeca Family</p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="order-1 lg:order-2">
            <p className="eyebrow">A Family Business Built on Trust</p>
            <h2 className="h2 mt-4">More Than a Contractor. A Partner Who Actually Cares.</h2>
            <p className="lead mt-5">
              For over {SITE.yearsExperience} years, families across {SITE.county} and all of New Jersey have trusted Ardeca to
              transform the spaces that stress them out. We're not a faceless company — we're a family, and we
              measure success by how relieved and proud you feel when we're done.
            </p>
            <ul className="mt-7 space-y-3.5">
              {VALUE_POINTS.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <span className="mt-0.5 grid place-items-center h-6 w-6 shrink-0 rounded-full bg-brand-orange/10 text-brand-orange">
                    <Check size={14} strokeWidth={3} />
                  </span>
                  <span className="text-brand-dark">{p}</span>
                </li>
              ))}
            </ul>
            <Link to="/contact" data-testid="value-cta" className="btn-cta mt-9">
              Start Your Project <ArrowRight size={17} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ===== SERVICE GRID ===== */}
      <section data-testid="services-grid" className="bg-brand-creamSoft border-y border-brand-cream">
        <div className="container-x py-20 md:py-28">
          <Reveal className="max-w-2xl">
            <p className="eyebrow">What We Do Best</p>
            <h2 className="h2 mt-4">Expert Remodeling Services Across New Jersey</h2>
            <p className="lead mt-5">
              Whatever's been bugging you about your home, we have the specialized craft to fix it — beautifully, and for good.
            </p>
          </Reveal>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {SERVICES.map((s, i) => (
              <Reveal key={s.slug} delay={(i % 3) * 0.08}>
                <ServiceCard service={s} className="h-full" />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section data-testid="how-it-works" className="container-x py-20 md:py-28">
        <Reveal className="text-center max-w-2xl mx-auto">
          <p className="eyebrow justify-center">Simple From Start to Finish</p>
          <h2 className="h2 mt-4">How It Works</h2>
          <p className="lead mt-5">Three relaxed steps from "I wish this were different" to "I love being home."</p>
        </Reveal>
        <div className="mt-14 grid md:grid-cols-3 gap-8">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.title} delay={i * 0.1}>
                <div className="relative rounded-2xl bg-white border border-brand-cream p-8 shadow-sm h-full">
                  <div className="grid place-items-center h-14 w-14 rounded-2xl bg-brand-orange text-white">
                    <Icon size={24} strokeWidth={1.9} />
                  </div>
                  <h3 className="h3 mt-6">{s.title}</h3>
                  <p className="mt-3 text-brand-muted leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ===== TRUST ===== */}
      <TrustBadges />

      {/* ===== TESTIMONIALS ===== */}
      <section data-testid="testimonials-section" className="container-x py-20 md:py-28">
        <Reveal className="text-center max-w-2xl mx-auto mb-12">
          <p className="eyebrow justify-center">Real NJ Homeowners</p>
          <h2 className="h2 mt-4">Loved by Families Across New Jersey</h2>
        </Reveal>
        <TestimonialSlider />
      </section>

      {/* ===== CTA ===== */}
      <CTAPanel />
    </>
  );
}
